import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TYPES = ["Quotation", "Sales Order", "Delivery Note", "Purchase Order", "Goods Receipt"];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function emptyItem() {
  return { _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, item_name: "", qty: 1, rate: 0, unit: "Nos", hsn: "", gst_rate: 0 };
}

function Inner() {
  const { activeId } = useCompany();
  const { oid } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const isEdit = !!oid;

  const [ledgers, setLedgers] = useState([]);
  const [form, setForm] = useState({
    order_type: search.get("type") || "Quotation",
    date: todayISO(),
    party_ledger_id: "",
    party_name: "",
    items: [emptyItem()],
    narration: "",
    discount: 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/ledgers`).then((r) => setLedgers(r.data || []));
    if (isEdit) {
      api.get(`/companies/${activeId}/orders/${oid}`).then((r) => {
        const d = r.data;
        // Tag each item with a stable client-side uid so React reconciliation is correct.
        d.items = (d.items || []).map((it) => ({
          ...it,
          _uid: it._uid || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `it-${Math.random()}`),
        }));
        setForm(d);
      });
    }
  }, [activeId, oid, isEdit]);

  const partyOptions = useMemo(() => {
    // Sundry Debtors for Sales-side orders, Sundry Creditors for Purchase-side
    const isPurchase = (form.order_type || "").startsWith("Purchase") || form.order_type === "Goods Receipt";
    const wanted = isPurchase ? "Sundry Creditors" : "Sundry Debtors";
    return ledgers.filter((L) => L.group === wanted || L.group === "Trade Receivables" || L.group === "Trade Payables");
  }, [ledgers, form.order_type]);

  const subTotal = useMemo(() => {
    return form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  }, [form.items]);
  const total = useMemo(() => subTotal - (Number(form.discount) || 0), [subTotal, form.discount]);

  const setItem = (idx, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: field === "qty" || field === "rate" || field === "gst_rate" ? Number(value) : value };
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.party_name) { toast.error("Please select a party"); return; }
    if (form.items.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const payload = { ...form, discount: Number(form.discount) || 0 };
      if (isEdit) {
        await api.put(`/companies/${activeId}/orders/${oid}`, payload);
        toast.success("Order updated");
      } else {
        const r = await api.post(`/companies/${activeId}/orders`, payload);
        toast.success(`Order ${r.data.order_no} created`);
      }
      nav("/orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title={isEdit ? `Edit ${form.order_type}` : `New ${form.order_type}`}
        subtitle="Pre-accounting document — converts to a Sales/Purchase voucher in one click"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/orders")}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-order">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-4 max-w-5xl" data-testid="order-form-page">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-gray-200 rounded bg-white p-4">
          <div>
            <Label className="text-xs">Order Type</Label>
            <select
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              value={form.order_type}
              onChange={(e) => setForm((f) => ({ ...f, order_type: e.target.value }))}
              data-testid="select-order-type"
              disabled={isEdit}
            >
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="input-order-date" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Party</Label>
            <select
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              value={form.party_ledger_id}
              onChange={(e) => {
                const lid = e.target.value;
                const L = ledgers.find((x) => x.id === lid);
                setForm((f) => ({ ...f, party_ledger_id: lid, party_name: L?.name || "" }));
              }}
              data-testid="select-party"
            >
              <option value="">— select party —</option>
              {partyOptions.map((L) => <option key={L.id} value={L.id}>{L.name} ({L.group})</option>)}
            </select>
          </div>
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Line Items</div>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="btn-add-item">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
            </Button>
          </div>
          <table className="w-full" data-testid="order-items-table">
            <thead>
              <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                <th className="text-left pl-3 py-1.5 font-medium">Item</th>
                <th className="text-left py-1.5 font-medium w-20">Unit</th>
                <th className="text-right py-1.5 font-medium w-20">Qty</th>
                <th className="text-right py-1.5 font-medium w-28">Rate</th>
                <th className="text-left py-1.5 font-medium w-20">HSN</th>
                <th className="text-right py-1.5 font-medium w-20">GST %</th>
                <th className="text-right py-1.5 pr-3 font-medium w-32">Amount</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={it._uid || `it-${i}`} className="border-b border-gray-100" data-testid={`order-item-row-${i}`}>
                  <td className="pl-3 py-1">
                    <Input value={it.item_name} onChange={(e) => setItem(i, "item_name", e.target.value)} placeholder="Item name" className="h-8 text-sm" data-testid={`item-name-${i}`} />
                  </td>
                  <td className="py-1"><Input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="h-8 text-sm" /></td>
                  <td className="py-1"><Input type="number" step="0.01" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="h-8 text-sm text-right" data-testid={`item-qty-${i}`} /></td>
                  <td className="py-1"><Input type="number" step="0.01" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} className="h-8 text-sm text-right" data-testid={`item-rate-${i}`} /></td>
                  <td className="py-1"><Input value={it.hsn} onChange={(e) => setItem(i, "hsn", e.target.value)} className="h-8 text-sm" /></td>
                  <td className="py-1"><Input type="number" step="0.01" value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} className="h-8 text-sm text-right" /></td>
                  <td className="py-1 pr-3 text-right font-mono text-sm text-gray-900">
                    {formatINR((Number(it.qty) || 0) * (Number(it.rate) || 0))}
                  </td>
                  <td className="py-1">
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(i)} className="text-red-600 hover:text-red-800" data-testid={`btn-remove-item-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan={6} className="pl-3 py-2 text-right text-xs text-gray-600 font-medium">Sub-total</td>
                <td className="py-2 pr-3 text-right font-mono text-sm font-semibold" data-testid="order-subtotal">{formatINR(subTotal)}</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={5}></td>
                <td className="py-1.5 text-right text-xs text-gray-600 font-medium">Discount</td>
                <td className="py-1.5 pr-3">
                  <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} className="h-7 text-sm text-right" data-testid="order-discount" />
                </td>
                <td></td>
              </tr>
              <tr className="bg-blue-50 border-t-2 border-blue-200">
                <td colSpan={6} className="pl-3 py-2 text-right text-xs text-blue-900 font-semibold uppercase tracking-wider">Total</td>
                <td className="py-2 pr-3 text-right font-mono text-base font-bold text-blue-900" data-testid="order-total">₹{formatINR(total)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="border border-gray-200 rounded bg-white p-4">
          <Label className="text-xs">Narration / Terms</Label>
          <textarea
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm min-h-20"
            value={form.narration}
            onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))}
            placeholder="Validity, delivery terms, payment terms, etc."
            data-testid="order-narration"
          />
        </div>
      </div>
    </>
  );
}

export default function OrderForm() {
  return <RequireCompany><Inner /></RequireCompany>;
}
